import * as React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Skeleton,
  IconButton,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CodeIcon from "@mui/icons-material/Code";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import CreateSquarespaceOrderDialog from "../components/admin/CreateSquarespaceOrderDialog";
import JsonDataModal from "../components/admin/JsonDataModal";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function SquarespaceOrders() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [ordersData, setOrdersData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [pagination, setPagination] = React.useState({
    page: 1,
    limit: 50,
    total: 0,
    offset: 0,
  });
  const [paginationUrls, setPaginationUrls] = React.useState({
    nextPageUrl: null,
    previousPageUrl: null,
  });
  const [pageHistory, setPageHistory] = React.useState([]);
  
  // Create order modal state
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [createFormData, setCreateFormData] = React.useState({
    customerId: "",
    pricingId: "",
    title: "",
    domain: "",
  });
  const [creatingOrder, setCreatingOrder] = React.useState(false);
  const [createError, setCreateError] = React.useState(null);

  // JSON debug modal state
  const [jsonModalOpen, setJsonModalOpen] = React.useState(false);
  const [selectedOrderJsonModalOpen, setSelectedOrderJsonModalOpen] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = React.useState(null);

  const handleViewOrderJson = (order) => {
    setSelectedOrder(order);
    setSelectedOrderJsonModalOpen(true);
  };

  const fetchOrders = React.useCallback(async (nextPageUrlParam = null) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      
      // Build the API URL with nextPageUrl as a query parameter
      const baseUrl = `${API_BASE_URL}/admin/squarespace/orders`;
      const fetchUrl = nextPageUrlParam 
        ? `${baseUrl}?nextPageUrl=${encodeURIComponent(nextPageUrlParam)}`
        : baseUrl;

      const response = await fetch(fetchUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to fetch orders: ${response.status} ${response.statusText}`
        );
      }

      const responseData = await response.json();
      // API now returns { data: [...], pagination: {...}, nextPageUrl: ... }
      const data = responseData.data || [];
      const paginationInfo = responseData.pagination || {};
      
      setOrdersData({
        data: data,
        pagination: paginationInfo,
      });
      
      // Update pagination URLs - check both root level and pagination object
      setPaginationUrls({
        nextPageUrl: responseData.nextPageUrl || paginationInfo.nextPageUrl || null,
        previousPageUrl: responseData.previousPageUrl || paginationInfo.previousPageUrl || null,
      });
      
      // Track page history for back navigation
      if (nextPageUrlParam && !pageHistory.includes(nextPageUrlParam)) {
        setPageHistory(prev => [...prev, nextPageUrlParam]);
      }
      
      // Update pagination state if pagination info exists
      if (paginationInfo) {
        setPagination({
          page: paginationInfo.page || 1,
          limit: paginationInfo.limit || data.length,
          total: paginationInfo.total || data.length,
          offset: paginationInfo.offset || 0,
        });
      } else {
        // If no pagination info, assume all data is shown
        setPagination({
          page: 1,
          limit: data.length,
          total: data.length,
          offset: 0,
        });
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleNextPage = () => {
    if (paginationUrls.nextPageUrl) {
      fetchOrders(paginationUrls.nextPageUrl);
    }
  };

  const handlePreviousPage = () => {
    if (paginationUrls.previousPageUrl) {
      fetchOrders(paginationUrls.previousPageUrl);
    } else if (pageHistory.length > 0) {
      // Go back in history
      const previousUrl = pageHistory[pageHistory.length - 1];
      setPageHistory(prev => prev.slice(0, -1));
      fetchOrders(previousUrl);
    } else {
      // Go back to first page (no nextPageUrl parameter)
      fetchOrders(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    if (!status) return "default";
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "fulfilled":
      case "completed":
        return "success";
      case "shipped":
        return "info";
      case "pending":
      case "processing":
        return "warning";
      case "cancelled":
      case "canceled":
        return "error";
      default:
        return "default";
    }
  };

  // Extract orders from data array
  const allOrders = Array.isArray(ordersData?.data) ? ordersData.data : [];
  
  // Filter by search query
  const orders = React.useMemo(() => {
    if (!searchQuery.trim()) return allOrders;
    
    const query = searchQuery.toLowerCase().trim();
    return allOrders.filter((order) => {
      const orderNumber = order.order_number || order.orderNumber || '';
      const customerName = order.customer_name || order.customerName || '';
      const customerEmail = order.customer_email || order.customerEmail || '';
      const id = order.id || '';
      const searchableText = `${orderNumber} ${customerName} ${customerEmail} ${id}`.toLowerCase();
      return searchableText.includes(query);
    });
  }, [allOrders, searchQuery]);

  // Handle create order (customerId-based)
  const handleCreateOrder = async () => {
    if (!createFormData.customerId || !createFormData.customerId.trim()) {
      setCreateError("Customer ID is required");
      return;
    }
    if (!createFormData.pricingId || !createFormData.pricingId.trim()) {
      setCreateError("Pricing ID is required");
      return;
    }
    if (!createFormData.title || !createFormData.title.trim()) {
      setCreateError("Website title is required");
      return;
    }

    setCreatingOrder(true);
    setCreateError(null);

    try {
      const token = await getAccessTokenSilently().catch(() => null);
      
      const newOrderItemWebSite = {
        pricingId: createFormData.pricingId.trim(),
        title: createFormData.title.trim(),
      };
      if (createFormData.domain && createFormData.domain.trim()) {
        newOrderItemWebSite.domains = [createFormData.domain.trim()];
      }
      
      const requestBody = {
        customerId: createFormData.customerId.trim(),
        newOrderItemWebSite,
      };

      const response = await fetch(`${API_BASE_URL}/admin/squarespace/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = "Failed to create order";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // Use default error message
        }
        throw new Error(errorMessage);
      }

      // Close dialog and reset form
      setCreateDialogOpen(false);
      setCreateFormData({
        customerId: "",
        pricingId: "",
        title: "",
        domain: "",
      });
      setCreateError(null);

      // Refresh the order list
      await fetchOrders();
    } catch (err) {
      console.error("Error creating order:", err);
      setCreateError(err.message || "Failed to create order");
    } finally {
      setCreatingOrder(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Orders
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Squarespace Orders"
              description="View and manage Squarespace orders"
              icon={<ShoppingCartIcon sx={{ color: "primary.main" }} />}
            >
              <Button
                variant="outlined"
                startIcon={<CodeIcon />}
                onClick={() => setJsonModalOpen(true)}
                sx={{ mr: 1 }}
              >
                View JSON
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCreateDialogOpen(true);
                  setCreateError(null);
                }}
                sx={{ mr: 2 }}
              >
                Create Order
              </Button>
              {!loading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Page:
                  </Typography>
                  <Chip
                    label={pagination.page || 1}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
            </SquarespacePageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <Card>
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Search by order number, customer name, or order ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Pagination controls below filters and above table */}
                  {(paginationUrls.nextPageUrl || paginationUrls.previousPageUrl || pageHistory.length > 0) && !loading && (
                    <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={handlePreviousPage}
                        disabled={!paginationUrls.previousPageUrl && pageHistory.length === 0 || loading}
                      >
                        Previous Page
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Page {pagination.page || 1}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleNextPage}
                        disabled={!paginationUrls.nextPageUrl || loading}
                      >
                        Next Page
                      </Button>
                    </Box>
                  )}

                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Order ID</TableCell>
                          <TableCell>Customer</TableCell>
                          <TableCell align="right">Items</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          Array.from({ length: 10 }).map((_, index) => (
                            <TableRow key={`skeleton-${index}`}>
                              <TableCell>
                                <Skeleton height={20} />
                              </TableCell>
                              <TableCell>
                                <Skeleton height={20} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton height={20} width={60} />
                              </TableCell>
                              <TableCell>
                                <Skeleton height={24} width={80} borderRadius={4} />
                              </TableCell>
                              <TableCell>
                                <Skeleton height={20} width={100} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton height={32} width={80} />
                              </TableCell>
                            </TableRow>
                          ))
                        ) : orders.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography color="text.secondary">
                                {searchQuery.trim() ? 'No orders found matching your search' : 'No orders available'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          orders.map((order) => (
                            <TableRow key={order.id} hover>
                              <TableCell>
                                <Link
                                  to={`/dashboard/squarespace/orders/${order.id}`}
                                  style={{ textDecoration: "none" }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontFamily="monospace"
                                    color="primary"
                                    sx={{ "&:hover": { textDecoration: "underline" } }}
                                  >
                                    {order.orderId || "N/A"}
                                  </Typography>
                                </Link>
                              </TableCell>
                              <TableCell>
                                {order.customerId ? (
                                  <Link
                                    to={`/dashboard/squarespace/customers/${order.customerId}`}
                                    style={{ textDecoration: "none" }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={500}
                                      color="primary"
                                      sx={{ "&:hover": { textDecoration: "underline" } }}
                                    >
                                      {order.customerId}
                                    </Typography>
                                  </Link>
                                ) : (
                                  <Typography variant="body2" fontWeight={500}>
                                    N/A
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {order.items?.length ?? 0}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={order.status ? (order.status.charAt(0).toUpperCase() + order.status.slice(1)) : 'N/A'}
                                  color={getStatusColor(order.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{formatDate(order.createdOn)}</TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewOrderJson(order)}
                                    aria-label="View order JSON"
                                    color="primary"
                                  >
                                    <CodeIcon fontSize="small" />
                                  </IconButton>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => navigate(`/dashboard/squarespace/orders/${order.orderId}`)}
                                  >
                                    View
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination controls below table */}
                  {(paginationUrls.nextPageUrl || paginationUrls.previousPageUrl || pageHistory.length > 0) && !loading && (
                    <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={handlePreviousPage}
                        disabled={!paginationUrls.previousPageUrl && pageHistory.length === 0 || loading}
                      >
                        Previous Page
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Page {pagination.page || 1}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleNextPage}
                        disabled={!paginationUrls.nextPageUrl || loading}
                      >
                        Next Page
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {!loading && (
              <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery.trim() ? (
                    <>Showing {orders.length} order{orders.length !== 1 ? 's' : ''} (filtered from {allOrders.length} total)</>
                  ) : (
                    <>Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} order{pagination.total !== 1 ? 's' : ''}</>
                  )}
                </Typography>
              </Box>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
      
      <CreateSquarespaceOrderDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateError(null);
        }}
        formData={createFormData}
        onFormDataChange={setCreateFormData}
        onCreate={handleCreateOrder}
        creating={creatingOrder}
        error={createError}
      />
      
      <JsonDataModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={{
          orders: orders,
          allOrders: ordersData?.data || [],
          total: orders.length,
          filteredTotal: orders.length,
          pagination: pagination,
          paginationUrls: paginationUrls,
        }}
        title="Orders JSON - All Orders"
      />
      
      <JsonDataModal
        open={selectedOrderJsonModalOpen}
        onClose={() => {
          setSelectedOrderJsonModalOpen(false);
          setSelectedOrder(null);
        }}
        data={selectedOrder}
        title={`Order JSON - ${selectedOrder?.id || 'Order Details'}`}
      />
    </>
  );
}
